import React, { useState, useEffect, useCallback } from 'react';
import { validate } from 'email-validator';
import { useTranslation } from '../../../hooks/useTranslation';
import { createInvoiceEmailTemplate } from '../EmailTemplate/InvoiceEmailTemplate';
import { useInvoiceStore } from '../../../stores/invoiceStore';
import { Send, CheckCircle, Loader2 } from 'lucide-react';
import { FloatingLabelInput } from '../../shared/FloatingLabelInput';
import { createTranslator } from '@dutchinvoice/translations';
import { motion, AnimatePresence } from 'framer-motion';
import { invoiceApi } from '../../../lib/api';
import { debounce } from '../../../utils/debounce';

interface EmailPreviewContainerProps {
  onEmailSent?: () => void;
}

export const EmailPreviewContainer: React.FC<EmailPreviewContainerProps> = ({ onEmailSent }) => {
  const { t } = useTranslation();
  const currentInvoice = useInvoiceStore((state) => state.currentInvoice);
  const settings = useInvoiceStore((state) => state.settings);
  const { updateEmailMessage, updateRecipient, updateInvoice } = useInvoiceStore();
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [emailHtml, setEmailHtml] = useState('');
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailInputValue, setEmailInputValue] = useState(currentInvoice.recipient.email || '');
  const [contactNameValue, setContactNameValue] = useState(currentInvoice.recipient.contactName || '');

  // Initialize input values when currentInvoice changes
  useEffect(() => {
    setEmailInputValue(currentInvoice.recipient.email || '');
    setContactNameValue(currentInvoice.recipient.contactName || '');
  }, [currentInvoice.recipient.email, currentInvoice.recipient.contactName]);

  // Debounced functions to update the store
  const debouncedUpdateEmail = useCallback(
    debounce((value: string) => updateRecipient('email', value), 500),
    [updateRecipient]
  );

  const debouncedUpdateContactName = useCallback(
    debounce((value: string) => updateRecipient('contactName', value), 500),
    [updateRecipient]
  );

  // Handle message updates from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'emailMessageUpdate') {
        // Extract everything after the first line (greeting)
        const lines = event.data.message.split('\n');
        const messageWithoutGreeting = lines.slice(1).join('\n').trim();
        console.log('Message Update:', {
          original: event.data.message,
          withoutGreeting: messageWithoutGreeting
        });
        updateEmailMessage(messageWithoutGreeting);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [updateEmailMessage]);

  // Handle email input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailInputValue(value);
    
    if (isEmailTouched) {
      setIsEmailValid(value === '' || validate(value));
    }
    
    debouncedUpdateEmail(value);
  };

  const handleEmailBlur = () => {
    setIsEmailTouched(true);
    const value = emailInputValue;
    setIsEmailValid(value === '' || validate(value));
    
    // Ensure the value is updated immediately when the field loses focus
    updateRecipient('email', value);
  };

  const handleContactNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setContactNameValue(value);
    debouncedUpdateContactName(value);
  };

  const handleContactNameBlur = () => {
    // Ensure the value is updated immediately when the field loses focus
    updateRecipient('contactName', contactNameValue);
  };

  // Update email preview when message changes
  useEffect(() => {
    // Skip during SSR
    if (typeof window === 'undefined') return;
    
    try {
      // Use the user's language setting for email content
      const emailT = createTranslator(settings.language);
      
      const mjmlTemplate = createInvoiceEmailTemplate({
        customMessage: currentInvoice.emailMessage || '',
        previewText: emailT('email.previewText'),
        invoice: currentInvoice,
        language: settings.language,
        t: emailT,
        logo: settings.logo,
        siteUrl: import.meta.env.VITE_SITE_URL || '',
        isPreview: true
      });
      
      // Set the raw MJML template directly
      setEmailHtml(mjmlTemplate);
    } catch (error) {
      console.error('Failed to create email template:', error);
    }
  }, [currentInvoice.emailMessage, currentInvoice.recipient.contactName, currentInvoice, settings.language, settings.logo]);

  // Extract email message from the template for editability
  const extractMessageFromTemplate = (html: string): string => {
    // Look for <!-- Email Message --> marker which contains the editable content
    const messageMatch = html.match(/<!-- Email Message -->([\s\S]*?)<!-- Invoice Content -->/);
    if (messageMatch && messageMatch[1]) {
      // Extract the actual text content from any HTML within
      const textContent = messageMatch[1]
        .replace(/<div[^>]*>/g, '')
        .replace(/<\/div>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
      return textContent;
    }
    return '';
  };

  // Handle iframe load event to inject editable content and scripts
  const handleIframeLoad = (iframe: HTMLIFrameElement) => {
    if (!iframe || !iframe.contentWindow || !iframe.contentDocument) return;
    
    const iframeDoc = iframe.contentDocument;
    
    // Find the message container in the iframe
    const messageContainer = iframeDoc.getElementById('editable-message');
    if (!messageContainer) return;
    
    // Make it editable
    messageContainer.contentEditable = 'true';
    messageContainer.style.outline = 'none';
    messageContainer.style.padding = '20px';
    messageContainer.style.maxWidth = '600px';
    messageContainer.style.margin = '0 auto';
    messageContainer.style.fontSize = '16px';
    messageContainer.style.lineHeight = '1.6';
    messageContainer.style.color = '#334155';
    
    // Add listeners to capture changes
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    
    messageContainer.addEventListener('input', () => {
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        const message = messageContainer.innerText || messageContainer.textContent || '';
        
        // Post message to parent window
        window.parent.postMessage({
          type: 'emailMessageUpdate',
          message: message
        }, '*');
      }, 500);
    });
  };

  const handleSendEmail = async () => {
    if (!currentInvoice.id || !emailInputValue || !isEmailValid) return;
    
    setIsSending(true);
    try {
      await invoiceApi.sendInvoiceEmail(
        currentInvoice.id,
        emailInputValue,
        currentInvoice.emailMessage,
        settings.language
      );
      
      // Update the local invoice status after successful email send
      updateInvoice({ status: 'sent' });
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onEmailSent?.();
      }, 4000);
    } catch (error) {
      console.error('Failed to send email:', error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-16rem)] relative">
      {/* Email Input - Fixed at top */}
      <div className="flex-none px-4 py-3 xs:px-5 xs:py-4 md:px-6 md:py-4 bg-white border-b border-gray-100">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          <div className="flex-1 grid grid-cols-1 gap-3 xs:gap-4 md:grid-cols-2 md:gap-4">
            <FloatingLabelInput
              type="email"
              name="recipient_email_address"
              id="recipient_email"
              value={emailInputValue}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              label={t('email.recipientEmail')}
              error={isEmailTouched && !isEmailValid ? t('email.invalidEmail') : undefined}
              touched={isEmailTouched}
              placeholder={currentInvoice.recipient.email ? "client@example.com" : ""}
              autoComplete="off"
              data-form-type="other"
            />
            <FloatingLabelInput
              type="text"
              name="contactName"
              id="contactName"
              value={contactNameValue}
              onChange={handleContactNameChange}
              onBlur={handleContactNameBlur}
              label={t('email.contactName')}
              placeholder={contactNameValue ? t('email.contactNamePlaceholder') : ""}
            />
          </div>
          <div className="flex items-center justify-center md:self-end md:mb-[8px]">
            <button
              className="btn-primary flex items-center gap-2 h-10 px-4 xs:h-11 xs:px-5 md:h-[46px] md:px-6 cursor-pointer transition-all duration-200 
                hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-500
                shadow-sm hover:shadow-md active:shadow-sm
                bg-primary-500
                border border-primary-400/20
                rounded-xl w-full md:w-auto"
              disabled={!isEmailValid || !emailInputValue || isSending}
              onClick={handleSendEmail}
            >
              {isSending ? (
                <Loader2 className="w-3.5 h-3.5 xs:w-4 xs:h-4 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5 xs:w-4 xs:h-4" />
              )}
              <span className="font-medium text-sm xs:text-base">{t('email.send')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Email Preview - Scrollable */}
      <div className="flex-1 overflow-hidden bg-gray-50 relative">
        <div className="absolute inset-0">
          <iframe
            key={emailHtml}
            srcDoc={`<!DOCTYPE html>
              <html>
                <head>
                  <meta charset="utf-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1">
                  <style>
                    body {
                      margin: 0;
                      font-family: system-ui, -apple-system, sans-serif;
                      background-color: #f8fafc;
                    }
                    img { max-width: 100%; height: auto; }

                    /* Hide Outlook conditionals */
                    [if], [endif], [if mso], [if !mso], [if gte mso], [if lte mso] {
                      display: none !important;
                    }
                  </style>
                </head>
                <body>
                  ${emailHtml.replace(/<!--\[if[^>]*>[\s\S]*?<!\[endif\]-->/g, '')
                    .replace(/<!-- Email Message -->[\s\S]*?<!-- Invoice Content -->/,
                      `<!-- Email Message --><div id="editable-message">${extractMessageFromTemplate(emailHtml)}</div><!-- Invoice Content -->`)}
                </body>
              </html>`}
            className="w-full h-full border-0"
            title="Email Preview"
            sandbox="allow-same-origin allow-scripts"
            ref={iframe => {
              if (iframe) {
                iframe.onload = () => handleIframeLoad(iframe);
              }
            }}
          />
        </div>
      </div>

      {/* Success Message Overlay */}
      <AnimatePresence mode="wait">
        {showSuccess && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{ willChange: 'opacity' }}
            className="absolute inset-0 bg-white/90 flex items-center justify-center z-10"
          >
            <div 
              className="bg-white p-8 rounded-2xl shadow-lg border border-green-100 text-center max-w-md"
            >
              <div 
                className="mx-auto mb-4 bg-green-50 w-16 h-16 rounded-full flex items-center justify-center"
              >
                <CheckCircle className="text-green-500 w-8 h-8" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-800 mb-2">{t('email.success.title')}</h3>
              <p className="text-gray-600">
                {t('email.success.description', { email: emailInputValue })}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmailPreviewContainer; 